import { PaletteCategory } from '@libs/types';
import { TCoreButtonOptions } from '../types/options';

const px = (value: unknown): string | undefined => (typeof value === 'number' ? `${value}px` : undefined);

type TColorRoles = {
    bg?: string;
    color?: string;
    border?: string;
};

// If you add a new palette role here, add it to `withBaseFallback` too
const resolveColorRoles = (variant: string | undefined, source: Partial<PaletteCategory> | undefined): TColorRoles => {
    if (!source) {
        return {};
    }
    if (variant === 'outlined') {
        return { bg: source.surface, color: source.main, border: source.outline };
    }
    if (variant === 'text') {
        return { color: source.main };
    }
    return { bg: source.main, color: source.onMain, border: source.outline };
};

const withBaseFallback = (base: Partial<PaletteCategory>, state: Partial<PaletteCategory> | undefined): Partial<PaletteCategory> | undefined =>
    state && {
        surface: state.surface ?? base.surface,
        main: state.main ?? base.main,
        onMain: state.onMain ?? base.onMain,
        outline: state.outline ?? base.outline,
    };

export const buildButtonCssVars = (palette: TCoreButtonOptions): Record<string, string> => {
    const margin = palette.margin ?? {};
    const padding = palette.padding ?? {};

    const base = resolveColorRoles(palette.variant, palette);
    const hover = resolveColorRoles(palette.variant, withBaseFallback(palette, palette.hovered));
    const active = resolveColorRoles(palette.variant, withBaseFallback(palette, palette.pressed));
    const disabled = resolveColorRoles(palette.variant, withBaseFallback(palette, palette.disabled));

    const vars: Record<string, string | undefined> = {
        // Size
        '--cw-btn-width': px(palette.width),
        '--cw-btn-max-width': palette.maxWidth,
        '--cw-btn-height': px(palette.height),
        '--cw-btn-max-height': palette.maxHeight,
        // Border & Radius
        '--cw-btn-radius': px(palette.borderRadius),
        '--cw-btn-border-width': px(palette.borderStroke),
        // Typography
        '--cw-btn-font-family': palette.font,
        '--cw-btn-font-weight': typeof palette.fontStyle?.fontWeight === 'number' ? palette.fontStyle.fontWeight.toString() : undefined,
        '--cw-btn-font-size': px(palette.fontStyle?.fontSize),
        '--cw-btn-line-height': px(palette.fontStyle?.lineHeight),
        '--cw-btn-letter-spacing': px(palette.fontStyle?.letterSpacing),
        '--cw-btn-text-align': palette.textAlign,
        '--cw-btn-max-lines': typeof palette.maxLines === 'number' ? String(palette.maxLines) : undefined,
        // Elevation (box-shadow level)
        '--cw-btn-elevation': typeof palette.elevation === 'number' ? String(palette.elevation) : undefined,
        '--cw-btn-box-shadow': palette.boxShadow,
        // Icon
        '--cw-btn-icon-size': px(palette.iconSize),
        '--cw-btn-gap': px(palette.gap),
        // margin
        '--cw-btn-margin-top': px(margin.top),
        '--cw-btn-margin-right': px(margin.right),
        '--cw-btn-margin-bottom': px(margin.bottom),
        '--cw-btn-margin-left': px(margin.left),
        // padding
        '--cw-btn-padding-top': px(padding.top),
        '--cw-btn-padding-right': px(padding.right),
        '--cw-btn-padding-bottom': px(padding.bottom),
        '--cw-btn-padding-left': px(padding.left),
        // default
        '--cw-btn-color': base.color,
        '--cw-btn-bg': base.bg,
        '--cw-btn-border-color': base.border,
        // hover
        '--cw-btn-color-hover': hover.color,
        '--cw-btn-bg-hover': hover.bg,
        '--cw-btn-border-color-hover': hover.border,
        // active / pressed
        '--cw-btn-color-active': active.color,
        '--cw-btn-bg-active': active.bg,
        '--cw-btn-border-color-active': active.border,
        // disabled
        '--cw-btn-color-disabled': disabled.color,
        '--cw-btn-bg-disabled': disabled.bg,
        '--cw-btn-border-color-disabled': disabled.border,
        '--cw-btn-cursor-disabled': palette.cursorDisabled,
        // transition
        '--cw-btn-width-transition': palette.widthTransition,
    };

    return Object.fromEntries(Object.entries(vars).filter((entry): entry is [string, string] => entry[1] !== undefined));
};
